import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { timeToMinutes, minutesToTime, toISODate } from "../../../../utils/date";
import ClassService from "../../../../services/class_service";
import { classDefaults, classSchema } from "../../../../models/class";

export const useClassesHandlers = ({
    idTenant,
    user,
    classes,
    activities,
    employees,
    areas,
    loadData
}) => {
    const [formState, setFormState] = useState(classDefaults);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const validate = async (data) => {
        try {
            await classSchema.validate(data, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            const newErrors = {};
            const newTouched = {};
            (err.inner || []).forEach(e => {
                newErrors[e.path] = e.message;
                newTouched[e.path] = true;
            });
            setErrors(newErrors);
            setTouched(newTouched);
            return false;
        }
    };

    const handleSave = useCallback(async () => {
        const isValid = await validate(formState);
        if (!isValid) {
            toast.warn("Preencha todos os campos obrigatórios.");
            return;
        }

        setIsSaving(true);
        try {
            const lookups = { activities, employees, areas };

            // Para edição, usa a data de hoje como ponto de corte
            // (só atualiza sessões futuras, nunca as passadas)
            const fromDate = formState.id
                ? toISODate(new Date())
                : null;

            await ClassService.save(formState, idTenant, user, lookups, classes, fromDate);
            toast.success(formState.id ? "Turma atualizada! Sessões futuras atualizadas." : "Turma(s) cadastrada(s) e sessões geradas!");
            setFormState(classDefaults);
            setErrors({});
            setTouched({});
            await loadData();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error("Erro ao salvar turma.");
        } finally {
            setIsSaving(false);
        }
    }, [formState, idTenant, user, activities, employees, areas, classes, loadData]);

    const handleDelete = useCallback(async () => {
        if (!formState.id) return;
        setIsSaving(true);
        try {
            // Usa a data de hoje como ponto de corte
            // (só exclui sessões da data de hoje em diante)
            const fromDate = toISODate(new Date());

            await ClassService.delete(
                formState.id,
                idTenant,
                user,
                formState.activityName || "",
                fromDate
            );
            toast.success("Turma removida e sessões futuras canceladas!");
            setFormState(classDefaults);
            setErrors({});
            setTouched({});
            await loadData();
        } catch (error) {
            console.error("Erro ao excluir:", error);

            // Tratamento especial: alunos matriculados
            if (error.code === "ENROLLED_MEMBERS") {
                toast.error(error.message, {
                    autoClose: 8000,
                    style: { minWidth: "400px" }
                });
            } else {
                toast.error("Erro ao excluir turma.");
            }
        } finally {
            setIsSaving(false);
        }
    }, [formState, idTenant, user, loadData]);

    const handleClassClick = useCallback((classItem) => {
        if (!classItem) return;
        setFormState({
            ...classItem,
            id: classItem.id || "",
            weekdays: classItem.weekday !== undefined ? [classItem.weekday] : [],
            durationMinutes: String(classItem.durationMinutes || "60"),
            maxCapacity: String(classItem.maxCapacity || "20"),
        });
        setErrors({});
        setTouched({});
    }, []);

    const handleStartTimeChange = (e) => {
        const nextStartTime = e.target.value;
        setFormState(prev => {
            const duration = Number(prev.durationMinutes) || 0;
            let endTime = prev.endTime;
            if (nextStartTime && duration > 0) {
                endTime = minutesToTime(timeToMinutes(nextStartTime) + duration);
            }
            return { ...prev, startTime: nextStartTime, endTime };
        });
    };

    const handleDurationChange = (e) => {
        const nextDuration = e.target.value;
        setFormState(prev => {
            const numDuration = Number(nextDuration) || 0;
            let endTime = prev.endTime;
            if (prev.startTime && numDuration > 0) {
                endTime = minutesToTime(timeToMinutes(prev.startTime) + numDuration);
            }
            return { ...prev, durationMinutes: nextDuration, endTime };
        });
    };

    const handleWeekdayToggle = (value) => {
        setFormState(prev => {
            if (prev.id) return { ...prev, weekday: value, weekdays: [value] };
            const currentWeekdays = prev.weekdays || [];
            const newWeekdays = currentWeekdays.includes(value)
                ? currentWeekdays.filter(v => v !== value)
                : [...currentWeekdays, value];
            return { ...prev, weekdays: newWeekdays };
        });
    };

    const handleNew = useCallback(() => {
        setFormState(classDefaults);
        setErrors({});
        setTouched({});
    }, []);

    return {
        formState,
        setFormState,
        errors,
        touched,
        isSaving,
        handleSave,
        handleDelete,
        handleClassClick,
        handleStartTimeChange,
        handleDurationChange,
        handleWeekdayToggle,
        handleNew
    };
};
